<script lang="ts" setup>
import { BuildingStory } from '@/models/BuildingStory.ts'
import FormElement from '@/components/FormElement.vue'
import InputSuffix from '@/components/InputSuffix.vue'
import formStore from '@/store/FormStore.ts'
import { ValidatorType } from '@/helper/ValidateDirective.ts'
import projectStore from '@/store/ProjectStore.ts'
import CheckBox from '@/components/CheckBox.vue'
import { ref } from 'vue'
import { Format } from '@/helper/Format.ts'

const props = defineProps<{
  index: number
  validator: ValidatorType
}>()

defineEmits<{
  (e: 'removeItem', index: number): void
}>()

const livingSpace = ref<string>(Format.round(formStore.data.buildingHull.stories[props.index].livingSpace)?.toString() || '')
const ceilingHeight = ref<string>(formStore.data.buildingHull.stories[props.index].ceilingHeight?.toString() || '')

defineExpose({
  validator: props.validator
})

function setBottomStory() {
  formStore.data.buildingHull?.stories
    .filter((s: BuildingStory) => s.bottomStory)
    .forEach((s: BuildingStory) => s.bottomStory = false)
  formStore.data.buildingHull.stories[props.index].bottomStory = true
}

function setTopStory() {
  formStore.data.buildingHull?.stories
    .filter((s: BuildingStory) => s.topStory)
    .forEach((s: BuildingStory) => s.topStory = false)
  formStore.data.buildingHull.stories[props.index].topStory = true
}

function setLivingSpace(val: string) {
  formStore.data.buildingHull.stories[props.index].livingSpace = val
}

function setCeilingHeight(val: string) {
  formStore.data.buildingHull.stories[props.index].ceilingHeight = val
}

</script>

<template>
  <div class="tr building-story-row">
    <div class="td column is--checkbox is--bottom-floor">
      <FormElement>
        <CheckBox
          :id="`building-hull-is-bottom-story-${index}`"
          v-model="formStore.data.buildingHull.stories[index].bottomStory"
          :disabled="formStore.data.buildingHull.stories[index].bottomStory || formStore.data.buildingHull.stories[index].topStory"
          :name="`building-hull-is-bottom-story-${index}`"
          @update:model-value="setBottomStory"
        />
      </FormElement>
    </div>

    <div class="td column is--checkbox is--top-floor">
      <FormElement>
        <CheckBox
          :id="`building-hull-is-top-story-${index}`"
          v-model="formStore.data.buildingHull.stories[index].topStory"
          :disabled="formStore.data.buildingHull.stories[index].bottomStory || formStore.data.buildingHull.stories[index].topStory"
          :name="`building-hull-is-top-story-${index}`"
          @update:model-value="setTopStory"
        />
      </FormElement>
    </div>

    <div class="td column is--living-area">
      <FormElement
        v-validate="projectStore.projectProperties.buildingHull.formValidationRule?.stories.livingArea"
        :error="validator[`buildingHullLivingArea${index}`]?.error"
      >
        <InputSuffix
          :id="`building-hull-living-area-${index}`"
          v-model="livingSpace"
          :placeholder="$t('step_heating_details.placeholder_living_area')"
          suffix=" m²"
          @update:model-value="setLivingSpace"
        />
      </FormElement>
    </div>

    <div class="td column is--ceiling-height">
      <FormElement
        v-validate="projectStore.projectProperties.buildingHull.formValidationRule?.stories.ceilingHeight"
        :error="validator[`buildingHullCeilingHeight${index}`]?.error"
      >
        <InputSuffix
          :id="`building-hull-ceiling-height-${index}`"
          v-model="ceilingHeight"
          :placeholder="$t('step_heating_details.placeholder_max_room_height')"
          suffix=" m"
          @update:model-value="setCeilingHeight"
        />
      </FormElement>
    </div>

    <div class="td column is--delete">
      <button
        :disabled="(formStore.data.buildingHull?.stories as BuildingStory[] || []).length < 2"
        class="icon-btn"
        type="button"
        @click="$emit('removeItem', index)"
      >
        <i class="ic-trash" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.column {
  &.is--checkbox {
    :deep(.form__input-wrapper) {
      justify-content: center;
    }
  }
}
</style>
